import React, { createContext, PropsWithChildren, useCallback, useMemo, useState } from "react";
import { v4 } from "uuid";

export type Dialog<DResponse, DProps> = React.ElementType<DProps & {
    onAccept: (value: DResponse) => void;
    onCancel: () => void;
}>;

export type DialogResponse<DResponse> = {
    accepted: false;
} | {
    accepted: true;
    result: DResponse;
};

export type DialogContextType = {
    open: <DResponse, DProps>(component: Dialog<DResponse, DProps>, additionalProps: DProps) => Promise<DialogResponse<DResponse>>;
};

type DialogState<DResponse, DProps> = {
    id: string;
    reject: () => void;
    resolve: (value: DResponse) => void;
    component: Dialog<DResponse, DProps>;
    additionalProps: DProps;
};

export const DialogContext = createContext<DialogContextType | undefined>(undefined);

const DialogProvider: React.FC<PropsWithChildren> = props => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [state, setState] = useState<DialogState<any, any>[]>();

    const open = useCallback(<DResponse, DProps>(component: Dialog<DResponse, DProps>, additionalProps: DProps): Promise<DialogResponse<DResponse>> => {
        const id = v4();
        return new Promise<DialogResponse<DResponse>>(resolve => {
            const dialogState: DialogState<DResponse, DProps> = {
                id: id,
                component: component,
                reject: () => {
                    resolve({ accepted: false });
                },
                resolve: value => {
                    resolve({
                        accepted: true,
                        result: value
                    });
                },
                additionalProps: additionalProps
            };
            setState(cur => ([
                ...(cur ?? []),
                dialogState
            ]));
        });
    }, []);

    const dialogContextValue: DialogContextType = useMemo(() => ({ open }), [open]);

    return (
        <DialogContext.Provider value={dialogContextValue}>
            {props.children}
            {state?.map(dialog => React.createElement(dialog.component, {
                ...dialog.additionalProps,
                onAccept: (value: unknown) => {
                    dialog.resolve(value);
                    setState(cur => cur?.filter(item => item.id !== dialog.id));
                },
                onCancel: () => {
                    dialog.reject();
                    setState(cur => cur?.filter(item => item.id !== dialog.id));
                },
                key: dialog.id
            }))}
        </DialogContext.Provider>
    );
};

export default DialogProvider;
